import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectorRef, ChangeDetectionStrategy, ElementRef, Input, Pipe, PipeTransform, Output, EventEmitter, AfterViewInit } from '@angular/core';
import { UntypedFormBuilder, Validators, ControlContainer, FormGroupDirective, FormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { interval, Subscription, Subject, BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { debounceTime, map, distinctUntilChanged, takeUntil, switchMap, catchError, finalize, shareReplay, tap } from 'rxjs/operators';

import { GlobalsService } from '../../services/globals.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service'
import { InventoryService } from '../../services/inventory.service';
import { CustomerService } from '../../services/customer.service';
import { Location } from '@angular/common'
import { NgbModal, ModalDismissReasons } from '@ng-bootstrap/ng-bootstrap';

import { Directive, HostListener } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';

import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { of } from 'rxjs';
import { OrdersService } from 'app/services/orders.service';

@Component({
	selector: 'app-inventory-adjustment',
	templateUrl: './inventory-adjustment.component.html',
	styleUrls: ['./inventory-adjustment.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class InventoryAdjustmentComponent implements OnInit, OnDestroy {
	@Input() stockid;
	@Input() cost;

	@Input() hidecost;
	@Input() refresh_qoh;
	@Input() empid_required: any = false;

	@Output() closeadj: any = new EventEmitter < boolean > ();
	@Output() newitemdata: any = new EventEmitter < boolean > ();
	@ViewChild('qty') qtyInput: ElementRef;
	@ViewChild('binInput') binInput: ElementRef;

	readonly itembins$ = new BehaviorSubject<any[]>([]);
	readonly bins$ = new BehaviorSubject<any[]>([]);
	readonly movements$ = new BehaviorSubject<any[]>([]);
	readonly locations$: Observable<any[]>;
	readonly config$: Observable<any>;
	
	readonly uiState$ = new BehaviorSubject({
		sending: false,
		savingBin: false,
		removingBin: false,
		loadingBins: false,
		editingBin: null as string | null,
		showAddBinInput: false
	});

	readonly totalQoh$ = combineLatest([this.itembins$, this.movements$]).pipe(
		map(([bins, movements]) => {
			const binTotal = bins.reduce((acc, bin) => acc + parseFloat(bin.quantity || '0'), 0);
			const movementTotal = movements.reduce((acc, m) => acc + parseFloat(m.qty || '0'), 0);
			return binTotal + movementTotal;
		}),
		shareReplay(1)
	);

	adjustmentForm: UntypedFormGroup;
	user: any = [];
	newBinValue = '';
	editingBinValue = '';
	originalBinValue = '';
	refreshing: any = false;
	validatingEmpCode: any = Subscription;
	validEmp: any = false;
	
	
	private destroy$ = new Subject<void>();
	private subscriptions: Subscription[] = [];
	private formSubscriptions: Subscription[] = [];
	private pendingAdjustment: Subscription;
	private binSearchCache = new Map<string, any[]>();

	private client: String = '';

	constructor(
		private route: ActivatedRoute, 
		public inventoryService: InventoryService,
		private ordersService: OrdersService, 
		private globalsService: GlobalsService, 
		private fb: UntypedFormBuilder,
		public globalSearchService: GlobalSearchService,
		private cdr: ChangeDetectorRef
	) {
		this.locations$ = this.globalSearchService.locations;
		this.config$ = this.globalSearchService.configsubscription;
		
		const userSub = this.globalSearchService.user.pipe(
			takeUntil(this.destroy$)
		).subscribe((user: any) => {
			this.user = user.user;
			this.cdr.markForCheck();
		});
		this.subscriptions.push(userSub);
	}

	ngOnInit(): void {
		this.initializeForm();
		this.loadInitialData();
		this.setupFormSubscriptions();
	}

	private initializeForm(): void {
		combineLatest([this.config$, this.locations$]).pipe(
			takeUntil(this.destroy$)
		).subscribe(([config, locations]) => {
			if (!this.adjustmentForm && config && this.user?.defaultlocation) {
				const binvalue = '';
				const location_selected = this.user.defaultlocation.loccode;

				this.canClientViewCost(config, this.user);
				this.gateEmpIdRequired(config);

				if (config.quickadjustment) {
					this.adjustmentForm = this.fb.group({
						stockid: [this.stockid, Validators.required],
						loccode: [location_selected, Validators.required],
						bin: [binvalue],
						qty: [0, Validators.required],
						reason: ['', Validators.required],
						cost: [this.cost]
					});
				} else {
					this.adjustmentForm = this.fb.group({
						stockid: [this.stockid, Validators.required],
						loccode: [location_selected, Validators.required],
						bin: [binvalue, Validators.required],
						qty: [0, Validators.required],
						reason: ['', Validators.required],
						cost: [this.cost]
					});
				}

				if (config && config.env && config.env.package == 'beauty') {
					this.adjustmentForm.get('bin').setValidators([]);
					this.adjustmentForm.get('reason').setValidators([]);
					this.adjustmentForm.updateValueAndValidity();
				}

				if(this.empid_required){
					this.adjustmentForm.addControl('empid', new FormControl('', [Validators.required]))

					//validate empcode exists on entry
					this.adjustmentForm.get('empid').valueChanges.pipe(debounceTime(350)).subscribe((empid)=>{
						if(this.validatingEmpCode){
							this.validatingEmpCode = false;
						}
						this.validatingEmpCode = this.ordersService.getEmployee({employee: empid}).subscribe((res)=>{							
							document.getElementById('empid').blur();

							if(res.success){
								this.validEmp = true;
								this.adjustmentForm.get('empid').setErrors(null);
							}else{
								this.validEmp = false;
								this.adjustmentForm.get('empid').setErrors(['Invalid Employee ID']);
								document.getElementById('empid').focus();
							}

							this.validatingEmpCode = false;
						})
					})
				}

				this.adjustmentForm.markAllAsTouched();
				this.cdr.markForCheck();
			}
		});
	}

	private loadInitialData(): void {
		const locationsSub = this.globalSearchService.locations.pipe(
			takeUntil(this.destroy$),
			switchMap((locs: any) => {
				const payload = {
					stockid: this.stockid,
					loccode: this.user.defaultlocation.loccode
				};
				return this.inventoryService.getItemBins(payload);
			}),
			catchError(error => {
				console.error('Failed to load bins:', error);
				this.globalSearchService.showNotification('Failed to load bins', 'danger', 'bottom', 'right');
				return of([]);
			})
		).subscribe((results: any) => {
			this.itembins$.next(results);
			this.cdr.markForCheck();
		});
		this.subscriptions.push(locationsSub);

		const binsSub = this.inventoryService.getBinSearch('').pipe(
			takeUntil(this.destroy$),
			catchError(error => {
				console.error('Failed to load bin search:', error);
				return of([]);
			})
		).subscribe((bins: any) => {
			this.bins$.next(bins);
			this.cdr.markForCheck();
		});
		this.subscriptions.push(binsSub);
	}


	selBin(bin: string): void {
		this.adjustmentForm.get('bin').setValue(bin);
		setTimeout(() => {
			if (this.qtyInput && this.qtyInput.nativeElement) {
				this.qtyInput.nativeElement.focus();
			}
		}, 100);
	}

	private resetForm(): void {
		const bins = this.itembins$.value;
		const binvalue = bins.length === 1 ? bins[0].bin : '';
		
		this.adjustmentForm.patchValue({
			bin: binvalue,
			qty: 0,
			reason: ''
		});

		this.updateUiState({ showAddBinInput: false });
		this.newBinValue = '';
		this.editingBinValue = '';
		this.originalBinValue = '';
		
		this.cdr.markForCheck();
	}

	private setupFormSubscriptions(): void {
		if (!this.adjustmentForm) {return;}

		const loccodeSub = this.adjustmentForm.get('loccode').valueChanges.pipe(
			takeUntil(this.destroy$),
			distinctUntilChanged(),
			tap(() => {
				this.updateUiState({ loadingBins: true });
				this.binSearchCache.clear();
			}),
			switchMap(newValue => {
				return this.inventoryService.getItemBins({ stockid: this.stockid, loccode: newValue }).pipe(
					finalize(() => this.updateUiState({ loadingBins: false })),
					catchError(error => {
						console.error('Failed to load bins for location:', error);
						this.globalSearchService.showNotification('Failed to load bins', 'danger', 'bottom', 'right');
						return of([]);
					})
				);
			})
		).subscribe((results: any) => {
			this.itembins$.next(results);
			this.cdr.markForCheck();
		});
		this.formSubscriptions.push(loccodeSub);

		const binSub = this.adjustmentForm.get('bin').valueChanges.pipe(
			takeUntil(this.destroy$),
			debounceTime(300),
			distinctUntilChanged(),
			switchMap(newValue => {
				if (newValue != '' && newValue != ' ') {
					newValue = newValue.toUpperCase();
					const loccode = this.adjustmentForm.get('loccode').value;
					const cacheKey = `${loccode}-${newValue}`;
					
					if (this.binSearchCache.has(cacheKey)) {
						return of(this.binSearchCache.get(cacheKey)!);
					}
					
					const search = { loccode, bin: newValue };
					return this.inventoryService.getBinSearch(search).pipe(
						tap(results => this.binSearchCache.set(cacheKey, results)),
						catchError(error => {
							console.error('Failed to search bins:', error);
							return of([]);
						})
					);
				}
				return of(this.bins$.value);
			})
		).subscribe((bins: any) => {
			this.bins$.next(bins);
			this.cdr.markForCheck();
		});
		this.formSubscriptions.push(binSub);

		if(this.refreshing && this.refresh_qoh){
			this.adjustmentForm.get('loccode').setValue(false);
			this.adjustmentForm.get('loccode').setValue(this.refreshing);
			this.refreshing = false;
		}
	}

	private cleanupFormSubscriptions(): void {
		this.formSubscriptions.forEach(sub => {
			if (sub && !sub.closed) {
				sub.unsubscribe();
			}
		});
		this.formSubscriptions = [];
	}

	private updateUiState(partial: Partial<typeof this.uiState$.value>): void {
		this.uiState$.next({ ...this.uiState$.value, ...partial });
		this.cdr.markForCheck();
	}

	trackByBin = (index: number, bin: any): string => {
		return `${bin.bin}-${bin.loccode}`;
	}

	trackByMovement = (index: number, movement: any): string => {
		return `${movement.bin}-${index}`;
	}

	trackByLocation = (index: number, location: any): string => {
		return location.loccode;
	}

	addMovement(): void {
		if (this.adjustmentForm.valid && this.adjustmentForm.get('qty').value != 0) {
			const move = this.adjustmentForm.value;
			const movements = [...this.movements$.value, move];
			this.movements$.next(movements);
			
			this.resetForm();
			
			this.config$.pipe(takeUntil(this.destroy$)).subscribe(config => {
				if (config.quickadjustment && !this.uiState$.value.sending) {
					this.makeAdjustment();
				}
			});
		} else {
			this.globalSearchService.showNotification('Invalid Adjustment', 'danger', 'bottom', 'right');
		}
	}

	binExists(): boolean {
		const bins = this.bins$.value;
		const binfilter = bins.filter((b: any) => {
			return b.bin == this.adjustmentForm.get('bin').value;
		})[0];

		return !!binfilter;
	}

	removeMovement(move: any): void {
		const movements = this.movements$.value.filter(m => m !== move);
		this.movements$.next(movements);
		this.cdr.markForCheck();
	}

	makeAdjustment(): void {
		const movements = this.movements$.value;
		if (movements && movements.length > 0) {
			if (this.pendingAdjustment && !this.pendingAdjustment.closed) {
				this.pendingAdjustment.unsubscribe();
			}
			
			this.updateUiState({ sending: true });
			const movementsToSend = [...movements];
			this.movements$.next([]);
			
			this.pendingAdjustment = this.inventoryService.adjustInventory(movementsToSend).subscribe({
				next: (results: any) => {
					this.updateUiState({ sending: false });
					this.inventoryService.loadItem(this.stockid).subscribe({
						next: (itemResults: any) => {
							this.newitemdata.emit(itemResults);
							if (movementsToSend.length > 0) {
								this.closeadj.emit(false);
							}

							if(this.stockid && itemResults?.item?.stockid === this.stockid && this.refresh_qoh){
								//refresh bins and store loc
								this.refreshing = this.adjustmentForm.get('loccode').value;
								this.subscriptions = [];
								this.initializeForm();
								this.loadInitialData();
								this.setupFormSubscriptions()
							}
						},
						error: (error: any) => {
							this.updateUiState({ sending: false });
							console.error('Failed to reload item after adjustment:', error);
							this.globalSearchService.showNotification('Adjustment saved but failed to reload item data', 'warning', 'bottom', 'right');
						}
					});
				},
				error: (error: any) => {
					this.updateUiState({ sending: false });
					const currentMovements = this.movements$.value;
					this.movements$.next([...movementsToSend, ...currentMovements]);
					console.error('Failed to save adjustment:', error);
					
					if (error.status === 0 || error.name === 'TimeoutError') {
						this.globalSearchService.showNotification('Connection timeout. Please check your internet connection and try again.', 'danger', 'bottom', 'right');
					} else if (error.status >= 500) {
						this.globalSearchService.showNotification('Server error. Please try again in a moment.', 'danger', 'bottom', 'right');
					} else {
						this.globalSearchService.showNotification('Failed to save adjustment: ' + (error.message || 'Unknown error'), 'danger', 'bottom', 'right');
					}


				}
			});
		}
	}

	editBin(bin: any): void {
		this.updateUiState({ editingBin: bin.bin });
		this.editingBinValue = bin.bin;
		this.originalBinValue = bin.bin;
	}

	removeBin(bin: any): void {
		if(bin.quantity === 0 || bin.quantity === '0') {
			if (this.uiState$.value.removingBin) {
				return;
			}

			const originalBin = { ...bin, bin: this.originalBinValue, stockid: this.stockid };
			const updatedBin = { ...bin, bin: this.editingBinValue, stockid: this.stockid };

			const payload = { originalBin, updatedBin };
			
			const currentBins = this.itembins$.value;
			const optimisticBins = currentBins.filter(b => b.bin !== this.originalBinValue);
			this.itembins$.next(optimisticBins);

			this.updateUiState({ editingBin: null, removingBin: true });
			this.editingBinValue = '';
			this.originalBinValue = '';

			this.inventoryService.removeBin(payload).pipe(
				switchMap((results: any) => {
					const color = (results.success) ? 'success' : 'danger';
					this.globalSearchService.showNotification(results.message, color, 'bottom', 'right');
					return this.inventoryService.getItemBins({ stockid: this.stockid, loccode: this.user.defaultlocation.loccode });
				}),
				finalize(() => this.updateUiState({ removingBin: false })),
				catchError(error => {
					console.error('Failed to remove bin:', error);
					this.globalSearchService.showNotification('Failed to remove bin', 'danger', 'bottom', 'right');
					this.itembins$.next(currentBins);
					return of([]);
				})
			).subscribe((results: any) => {
				if (results.length > 0) {
					this.itembins$.next(results);
				}
				this.cdr.markForCheck();
			});
		} else {
			this.globalSearchService.showNotification('Bin Is Not Empty', 'danger', 'bottom','right');
		}
	}

	saveBin(bin: any): void {
		if (this.uiState$.value.savingBin) {
			return;
		}

		const originalBin = { ...bin, bin: this.originalBinValue, stockid: this.stockid };
		const updatedBin = { ...bin, bin: this.editingBinValue, stockid: this.stockid };
		const payload = { originalBin, updatedBin };

		const currentBins = this.itembins$.value;
		const optimisticBins = currentBins.map(b => 
			b.bin === this.originalBinValue ? { ...b, bin: this.editingBinValue } : b
		);
		this.itembins$.next(optimisticBins);

		this.updateUiState({ editingBin: null, savingBin: true });
		this.editingBinValue = '';
		this.originalBinValue = '';

		this.inventoryService.changeBinCode(payload).pipe(
			switchMap((results: any) => {
				const color = (results.success) ? 'success' : 'danger';
				this.globalSearchService.showNotification(results.message, color, 'bottom', 'right');
				return this.inventoryService.getItemBins({ stockid: this.stockid, loccode: this.user.defaultlocation.loccode });
			}),
			finalize(() => this.updateUiState({ savingBin: false })),
			catchError(error => {
				console.error('Failed to save bin:', error);
				this.globalSearchService.showNotification('Failed to save bin', 'danger', 'bottom', 'right');
				this.itembins$.next(currentBins);
				return of([]);
			})
		).subscribe((results: any) => {
			if (results.length > 0) {
				this.itembins$.next(results);
			}
			this.cdr.markForCheck();
		});
	}

	cancelEdit(): void {
		this.updateUiState({ editingBin: null });
		this.editingBinValue = '';
		this.originalBinValue = '';
	}


	createBin(): void {
		if (this.newBinValue && !this.uiState$.value.savingBin) {
			const newBin = {
				loccode: this.user.defaultlocation.loccode,
				bin: this.newBinValue,
				stockid: this.stockid,
				quantity: 0,
				reorderlevel: 0,
				managed: 0,
				canupd: 1,
				quantityreq: 0
			};

			const currentBins = this.itembins$.value;
			this.itembins$.next([...currentBins, newBin]);

			this.updateUiState({ savingBin: true });

			this.inventoryService.addBinSimple(newBin).pipe(
				finalize(() => this.updateUiState({ savingBin: false })),
				catchError(error => {
					console.error('Failed to create bin:', error);
					this.globalSearchService.showNotification('Failed to create bin', 'danger', 'bottom', 'right');
					this.itembins$.next(currentBins);
					return of({ success: false });
				})
			).subscribe((result: any) => {
				if (result.success) {
					this.updateUiState({ showAddBinInput: false });
					this.newBinValue = '';
					this.globalSearchService.showNotification(result.message, 'success', 'bottom', 'right');
				} else if (result.message) {
					this.globalSearchService.showNotification(result.message, 'danger', 'bottom', 'right');
					this.itembins$.next(currentBins);
				}
				this.cdr.markForCheck();
			});
		}
	}

	selectText(inputElement: HTMLInputElement): void {
		inputElement.select();
	}


	/** Summit (`env.client`) is the only tenant that uses employee IDs, so the empid prompt is gated to it. */
	private gateEmpIdRequired(config): void {
		if(this.empid_required){
			this.empid_required = config?.env?.client === 'summit';
		}
	}

	private canClientViewCost(config, user){
		this.client = config.env.client;
		const usergroup = Number(user.usergroup);
		const can_view_summit = []; //add user group id to allow viewing of cost.

		if(this.hidecost){
			switch(this.client){
				case 'summit':
					this.hidecost = can_view_summit.includes(usergroup) ? false : true;
					break;
				default:	
					this.hidecost = false;
				break;
			}
		}
	}

	ngOnDestroy(): void {
		this.cleanupFormSubscriptions();
		
		this.subscriptions.forEach(sub => {
			if (sub && !sub.closed) {
				sub.unsubscribe();
			}
		});
		this.subscriptions = [];

		if (this.pendingAdjustment && !this.pendingAdjustment.closed) {
			this.pendingAdjustment.unsubscribe();
		}

		this.binSearchCache.clear();
		
		this.itembins$.complete();
		this.bins$.complete();
		this.movements$.complete();
		this.uiState$.complete();

		this.destroy$.next();
		this.destroy$.complete();
	}
}